/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React from "react";
import { useForm } from "../../../provider/Form/FormProvider";
import Typography from "@mui/material/Typography";
import { Box, Grid, Paper } from "@mui/material";
import FormLegend from "../Common/FormLegend/FormLegend";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import LegalbirdLoader from "../../ContentLoader/LegalbirdLoader";
import wizardStyle from "../wizardStyle";
import Hidden from "@mui/material/Hidden";
import MultipleFileUpload from "../Common/FileUpload/MultipleFileUpload";
import { filterMediaObjectsByFieldname } from "../../../services/mediaObjects";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import FormFader from "../Common/Fader/FormFader";
import { fetchCollection, queryKeys } from "../../../services/reactQuery/reactQueryService";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import FormSubmitProps from "../../../types/FormSubmitProps";

type DocumentsProps = {
  product: AbstractCase;
  submitProps: FormSubmitProps;
};
export default function Documents({ product, submitProps }: DocumentsProps) {
  const urlParams = {
    product: product.name,
    productId: product.id,
  };
  const { data: mediaObjectCollection, isLoading } = useQuery(queryKeys.collection("/media_objects", urlParams), () =>
    fetchCollection("/media_objects", urlParams)
  );

  const queryClient = useQueryClient();
  const { handleSubmit } = useForm();
  const refreshMediaObjectCollection = () => queryClient.invalidateQueries(queryKeys.collection("/media_objects", urlParams));

  if (isLoading) {
    return <LegalbirdLoader />;
  }

  return (
    <FormFader>
      <Paper sx={{ padding: "2rem" }}>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Hidden smUp>
            <Typography variant="h4" sx={wizardStyle.headline}>
              Unterlagen
            </Typography>
          </Hidden>
          <Grid container justifyContent={"flex-start"} alignItems={"baseline"} spacing={2}>
            <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
              <FormLegend>
                Bitte laden Sie alle Dokumente (Fotos, Screenshots, Schriftwechsel, Rechnungen...) hoch, die für Ihr Anliegen wichtig sind. Sollten Ihnen
                bislang keine Dokumente vorliegen, können Sie einfach in den nächsten Schritt wechseln.
              </FormLegend>
            </Grid>
            <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
              <Typography variant="h5">Fotos</Typography>
              <Typography paragraph>Laden Sie hier bitte Fotos hoch, die den Hergang dokumentieren oder beweisen können.</Typography>
              <MultipleFileUpload
                product={product}
                fieldname={"generalCivilLawPhotos"}
                description={"Foto"}
                mediaObjectCollection={filterMediaObjectsByFieldname(mediaObjectCollection, "generalCivilLawPhotos")}
                fetchMediaObjects={refreshMediaObjectCollection}
              />
            </Grid>
            <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
              <Typography variant="h5">Schriftwechsel</Typography>
              <Typography paragraph>
                Wenn es Schriftwechsel mit der Gegenseite gab, dann laden Sie diesen bitte hier hoch. Dies können Screenshots von WhatsApp-Verläufen, E-Mails
                oder Briefe sein.
              </Typography>
              <MultipleFileUpload
                product={product}
                fieldname={"generalCivilLawCorrespondences"}
                description={"Schriftwechsel mit Gegenseite"}
                mediaObjectCollection={filterMediaObjectsByFieldname(mediaObjectCollection, "generalCivilLawCorrespondences")}
                fetchMediaObjects={refreshMediaObjectCollection}
              />
            </Grid>
            {product.policeInvolved && <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
                <Typography variant="h5">Polizeibericht</Typography>
                <Typography paragraph>
                  Sollte bereits ein Polizeibericht oder eine Anzeigenbestätigung vorliegen, lassen Sie uns diesen bitte zukommen.
                </Typography>
                <MultipleFileUpload
                  product={product}
                  fieldname={"policeReports"}
                  description={"Polizeibericht"}
                  mediaObjectCollection={filterMediaObjectsByFieldname(mediaObjectCollection, "policeReports")}
                  fetchMediaObjects={refreshMediaObjectCollection}
                />
              </Grid>
            }
            <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
              <Typography variant="h5">Rechnungen</Typography>
              <Typography paragraph>Laden Sie hier bitte Rechnungen hoch, die im Zusammenhang mit dem Vorfall angefallen sind.</Typography>
              <MultipleFileUpload
                product={product}
                fieldname={"generalCivilLawBills"}
                description={"Rechnung"}
                mediaObjectCollection={filterMediaObjectsByFieldname(mediaObjectCollection, "generalCivilLawBills")}
                fetchMediaObjects={refreshMediaObjectCollection}
              />
            </Grid>
            <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
              <Typography variant="h5">Weitere Unterlagen</Typography>
              <Typography paragraph>Jegliche andere Dokumente, die zum Verständnis des Falls oder als Beweis sinnvoll sind.</Typography>
              <MultipleFileUpload
                product={product}
                fieldname={"generalCivilLawOtherDocuments"}
                description={"Weitere Unterlagen"}
                mediaObjectCollection={filterMediaObjectsByFieldname(mediaObjectCollection, "generalCivilLawOtherDocuments")}
                fetchMediaObjects={refreshMediaObjectCollection}
              />
            </Grid>
          </Grid>
          <br />
          <br />
          <FormSubmit {...submitProps} />
        </Box>
      </Paper>
    </FormFader>
  );
}
